import {
    type App,
    computed,
    type ComputedRef,
    inject,
    type Ref,
    ref,
} from 'vue'
import { RegionCode } from '@/graphql/api'
import { includes } from '@/helpers'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import {
    defaultRegion,
    getRoutingRegion,
    noPrefixRegion,
    type RoutingRegion,
} from '@/plugins/region2.ts'
import { getIsActiveRegion } from './region2'
import type { RedirectFunction } from 'wucdbm-vite-vue-ssr'

export type SelectRegionFunction = (regionCode: RegionCode) => Promise<void>

export { noPrefixRegion, getRoutingRegion, defaultRegion }

export type { RoutingRegion }

const pluginKey = 'app.plugin.region'

export type RegionPlugin = {
    regionCode: Readonly<Ref<RegionCode>>
    selectRegion: SelectRegionFunction
    routingRegion: Readonly<Ref<RoutingRegion>>
}

export const RegionSlugs: RoutingRegion[] = Object.values(RegionCode).map(
    (r) => r.toLowerCase() as RoutingRegion
)
export { getIsActiveRegion }

export function installRegionPlugin(
    app: App,
    options: {
        initial: RegionCode
        router: Router
        redirect: RedirectFunction
    }
): RegionPlugin {
    const { router, redirect } = options

    const regionCode = ref<RegionCode>(options.initial)

    const selectRegion: SelectRegionFunction = async (r) => {
        const isRegionChanged = regionCode.value !== r

        if (isRegionChanged) {
            const isCurrentRegionActive = getIsActiveRegion(regionCode.value)
            regionCode.value = r

            const newHref = // @ts-expect-error alabala
                router.resolve({
                    ...router.currentRoute.value,
                    params: {
                        ...router.currentRoute.value.params,
                        region: getRoutingRegion(r),
                    },
                }).href

            await redirect(newHref, isCurrentRegionActive ? 302 : 301)
        }
    }

    const routingRegion: ComputedRef<RoutingRegion> = computed(
        (): RoutingRegion => {
            return getRoutingRegion(regionCode.value)
        }
    )

    const pluginData: RegionPlugin = {
        routingRegion,
        regionCode,
        selectRegion,
    }
    const plugin: RegionPlugin = Object.freeze(pluginData)

    app.provide(pluginKey, plugin)

    return plugin
}

export function useRegionPlugin(): RegionPlugin {
    return inject(pluginKey) as RegionPlugin
}

export function isAppRegion(region: string): region is RegionCode {
    return includes(Object.values(RegionCode), region)
}

// We need satisfies in order to have TS evaluate their types as literals

export function getRegionFromRoute(
    currentRoute: RouteLocationNormalizedLoaded
): RegionCode {
    const routeRegion = currentRoute.params['region']

    if (typeof routeRegion === 'string') {
        const possiblyRegion = routeRegion.toUpperCase()

        if (isAppRegion(possiblyRegion)) {
            return possiblyRegion
        }
    }

    return noPrefixRegion
}

export function getRegionFromUrl(url: URL): RegionCode {
    const path = url.pathname

    const parts = path.split('/')

    const region = parts[1]

    if (undefined === region) {
        return noPrefixRegion
    }

    if ('' === region) {
        return noPrefixRegion
    }

    const possiblyRegion = region.toUpperCase()

    if (isAppRegion(possiblyRegion)) {
        return possiblyRegion
    }

    return noPrefixRegion
}

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    describe('getRegionFromUrl', () => {
        test('getRegionFromUrl', () => {
            expect(getRegionFromUrl(new URL('https://local'))).toBe(
                RegionCode.Uk
            )
            expect(getRegionFromUrl(new URL('https://local/'))).toBe(
                RegionCode.Uk
            )
            expect(getRegionFromUrl(new URL('https://local/uk'))).toBe(
                RegionCode.Uk
            )
            expect(getRegionFromUrl(new URL('https://local/us'))).toBe(
                RegionCode.Us
            )
            expect(getRegionFromUrl(new URL('https://local/int'))).toBe(
                RegionCode.Int
            )
            expect(getRegionFromUrl(new URL('https://local/alabala'))).toBe(
                RegionCode.Uk
            )
            expect(getRegionFromUrl(new URL('https://local/uk/alabala'))).toBe(
                RegionCode.Uk
            )
            expect(getRegionFromUrl(new URL('https://local/us/alabala'))).toBe(
                RegionCode.Us
            )
            expect(getRegionFromUrl(new URL('https://local/int/alabala'))).toBe(
                RegionCode.Int
            )
        })
    })
}
